import { View, Text } from 'react-native'
import { BrandedRefreshControl } from '@/components/ui/BrandedRefreshControl'
import { ScrollView } from 'react-native-gesture-handler'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'expo-router'
import { useScreenInsets } from '@/lib/hooks/use-screen-insets'
import Animated from 'react-native-reanimated'
import { DollarSign, TrendingUp, Clock, Landmark, ChevronRight } from '@/components/ui/icons'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { EmptyState } from '@/components/ui/EmptyState'
import { Skeleton } from '@/components/ui/Skeleton'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { Pressable } from '@/components/ui/Pressable'
import { useAuth } from '@/lib/hooks/use-auth'
import { useEarningsSummary, useHostStats } from '@/lib/hooks/use-hosting-dashboard'
import { useSemanticColors, withAlpha, SPACING, RADIUS } from '@/lib/theme/tokens'
import { formatCurrency, formatPrice } from '@/lib/utils/format'
import { COLORS, FONTS } from '@/lib/brand'
import { WEB_SHADOWS } from '@/lib/theme/web-shadow'
import { useTheme } from '@/lib/hooks/use-theme'
import { useEntrance } from '@/lib/hooks/use-entrance'

function StatBox({
  label,
  value,
  icon: Icon,
  colors,
}: {
  label: string
  value: string
  icon: typeof DollarSign
  colors: ReturnType<typeof useSemanticColors>
}) {
  return (
    <View
      style={{
        flex: 1,
        backgroundColor: colors.surface,
        borderRadius: RADIUS.lg,
        padding: SPACING.md,
        alignItems: 'center',
        gap: SPACING.xs,
        ...WEB_SHADOWS.sm,
      }}
    >
      <Icon size={20} color={COLORS.coral.DEFAULT} strokeWidth={1.8} />
      <Text style={{ fontSize: 18, fontFamily: FONTS.bodyBold, color: colors.textPrimary }}>
        {value}
      </Text>
      <Text
        style={{
          fontSize: 12,
          fontFamily: FONTS.body,
          color: colors.textSecondary,
          textAlign: 'center',
        }}
      >
        {label}
      </Text>
    </View>
  )
}

export default function EarningsScreen() {
  const enter = useEntrance()
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const insets = useScreenInsets()
  const router = useRouter()
  const { user } = useAuth()
  const hostId = user?.id ?? ''
  const { stats } = useHostStats(hostId)
  const { earnings, isLoading, isRefetching, error, refetch } = useEarningsSummary(hostId, 6)

  const maxCents = earnings?.months.reduce((max, m) => Math.max(max, m.totalCents), 0) ?? 1

  return (
    <ScreenContainer scroll={false}>
      <ScreenHeader title={t('earnings.title')} />

      <ScrollView
        contentContainerStyle={{
          // flexGrow lets the empty / error branches below claim the full
          // height instead of stacking into the top third of the screen.
          flexGrow: 1,
          paddingHorizontal: SPACING.lg,
          paddingBottom: insets.bottom + SPACING.xl,
        }}
        showsVerticalScrollIndicator={false}
        refreshControl={<BrandedRefreshControl refreshing={isRefetching} onRefresh={refetch} />}
      >
        {isLoading ? (
          <View style={{ gap: SPACING.md }}>
            <View style={{ flexDirection: 'row', gap: SPACING.sm }}>
              <Skeleton width="48%" height={100} borderRadius={RADIUS.lg} />
              <Skeleton width="48%" height={100} borderRadius={RADIUS.lg} />
            </View>
            <Skeleton width="100%" height={200} borderRadius={RADIUS.lg} />
          </View>
        ) : error ? (
          // "$0 · Aún no tenés ganancias" is a claim about someone's money.
          // A failed request must never make it — a host reading that would
          // reasonably think their earnings had been wiped.
          <View style={{ flex: 1, justifyContent: 'center' }}>
            <EmptyState
              variant="aurora"
              title={t('common.error_title')}
              description={t('common.error_description')}
              action={{ label: t('common.retry'), onPress: () => void refetch() }}
            />
          </View>
        ) : !earnings || earnings.grandTotalCents === 0 ? (
          // The message is centred in the space it actually has, and the
          // payout row is anchored to the bottom of the safe area instead of
          // being bolted under the empty state with 400pt of nothing below it.
          <View style={{ flex: 1 }}>
            <View style={{ flex: 1, justifyContent: 'center' }}>
              <EmptyState
                variant="bloom"
                illustration={
                  <DollarSign size={36} color={COLORS.coral.DEFAULT} strokeWidth={1.6} />
                }
                title={t('earnings.no_earnings')}
                description={t('earnings.no_earnings_desc')}
                action={{
                  label: t('hosting.my_listings'),
                  onPress: () => router.push('/hosting/listings' as never),
                }}
              />
            </View>
            <ManagePayoutAccountsCard colors={colors} isDark={isDark} />
          </View>
        ) : (
          <>
            {/* Stats row */}
            <Animated.View
              collapsable={false}
              entering={enter(1)}
              style={{ flexDirection: 'row', gap: SPACING.sm }}
            >
              <StatBox
                label={t('earnings.all_time')}
                value={formatPrice(earnings.grandTotalCents ?? 0)}
                icon={DollarSign}
                colors={colors}
              />
              <StatBox
                label={t('hosting.pending_payouts')}
                value={formatPrice(stats?.pendingPayoutCents ?? 0)}
                icon={Clock}
                colors={colors}
              />
              <StatBox
                label={t('hosting.rented_days')}
                value={`${earnings.grandTotalRentedDays}`}
                icon={TrendingUp}
                colors={colors}
              />
            </Animated.View>

            {/* Manage payout accounts */}
            <ManagePayoutAccountsCard colors={colors} isDark={isDark} />

            {/* Bar chart */}
            <Animated.View
              collapsable={false}
              entering={enter(2)}
              style={{
                marginTop: SPACING.lg,
                backgroundColor: colors.surface,
                borderRadius: RADIUS.lg,
                padding: SPACING.md,
                ...WEB_SHADOWS.sm,
              }}
            >
              <Text
                style={{
                  fontSize: 16,
                  fontFamily: FONTS.bodyBold,
                  color: colors.textPrimary,
                  marginBottom: SPACING.md,
                }}
              >
                {t('earnings.monthly_breakdown')}
              </Text>
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'flex-end',
                  gap: SPACING.xs,
                  height: 140,
                }}
              >
                {earnings.months.map((month) => {
                  const barHeight =
                    maxCents > 0 ? Math.max((month.totalCents / maxCents) * 120, 4) : 4
                  return (
                    <View key={month.month} style={{ flex: 1, alignItems: 'center' }}>
                      <Text
                        style={{
                          fontSize: 12,
                          fontFamily: FONTS.body,
                          color: colors.textSecondary,
                          marginBottom: SPACING.xs,
                        }}
                      >
                        {formatPrice(month.totalCents ?? 0)}
                      </Text>
                      <View
                        style={{
                          width: '70%',
                          height: barHeight,
                          borderRadius: RADIUS.xs,
                          backgroundColor:
                            month.totalCents > 0 ? COLORS.coral.DEFAULT : colors.border,
                        }}
                      />
                      <Text
                        style={{
                          fontSize: 12,
                          fontFamily: FONTS.body,
                          color: colors.textMuted,
                          marginTop: SPACING.xs,
                        }}
                      >
                        {month.month.slice(5)}
                      </Text>
                    </View>
                  )
                })}
              </View>
            </Animated.View>

            {/* Transaction list */}
            <Animated.View
              collapsable={false}
              entering={enter(3)}
              style={{
                marginTop: SPACING.lg,
                backgroundColor: colors.surface,
                borderRadius: RADIUS.lg,
                padding: SPACING.md,
                ...WEB_SHADOWS.sm,
              }}
            >
              <Text
                style={{
                  fontSize: 16,
                  fontFamily: FONTS.bodyBold,
                  color: colors.textPrimary,
                  marginBottom: SPACING.sm,
                }}
              >
                {t('earnings.transaction_history')}
              </Text>
              {earnings.months
                .filter((m) => m.rentalCount > 0)
                .map((month) => (
                  <View
                    key={month.month}
                    style={{
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      paddingVertical: SPACING.sm,
                      borderBottomWidth: 1,
                      borderBottomColor: colors.border,
                    }}
                  >
                    <Text
                      style={{ fontSize: 14, fontFamily: FONTS.body, color: colors.textSecondary }}
                    >
                      {month.month}
                    </Text>
                    <View style={{ alignItems: 'flex-end' }}>
                      <Text
                        style={{
                          fontSize: 14,
                          fontFamily: FONTS.bodyBold,
                          color: colors.textPrimary,
                        }}
                      >
                        {formatCurrency(month.totalCents ?? 0)}
                      </Text>
                      <Text
                        style={{ fontSize: 12, fontFamily: FONTS.body, color: colors.textMuted }}
                      >
                        {t('earnings.total_rentals', { count: month.rentalCount })}
                      </Text>
                    </View>
                  </View>
                ))}
            </Animated.View>
          </>
        )}
      </ScrollView>
    </ScreenContainer>
  )
}

/**
 * The same row exists on the Anfitrión dashboard. It used to be drawn
 * differently here — RADIUS.lg, no border, a 36pt icon tile — so one tap
 * apart the user met two designs of one control. This is now a
 * pixel-for-pixel match of the dashboard row (RADIUS.md, 1px border, 40pt
 * tile, unclamped 17pt-leaded description) including its tile hue, which is
 * the brand purple in both themes on both screens.
 */
function ManagePayoutAccountsCard({
  colors,
  isDark,
}: {
  colors: ReturnType<typeof useSemanticColors>
  isDark: boolean
}) {
  const { t } = useTranslation()
  const router = useRouter()

  return (
    <Pressable
      onPress={() => router.push('/hosting/payouts' as never)}
      accessibilityRole="button"
      accessibilityLabel={t('payouts.manage_accounts')}
      testID="earnings.manage-payouts"
      style={{
        marginTop: SPACING.lg,
        backgroundColor: colors.surface,
        borderRadius: RADIUS.md,
        borderWidth: 1,
        borderColor: colors.border,
        padding: SPACING.md,
        flexDirection: 'row',
        alignItems: 'center',
        gap: SPACING.md,
        ...WEB_SHADOWS.sm,
      }}
    >
      {/* Purple in BOTH themes, exactly like the identical row on the
          Anfitrión dashboard. The dark branch used to paint this tile coral on
          coral, so one tap apart the same control changed hue. `textBrand` is
          the brand purple at a lightness that survives a dark surface, and the
          tile is that same hue at low alpha. */}
      <View
        style={{
          width: 40,
          height: 40,
          borderRadius: RADIUS.md,
          backgroundColor: withAlpha(colors.textBrand, isDark ? 0.18 : 0.1),
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Landmark size={20} color={colors.textBrand} strokeWidth={1.8} />
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text
          style={{
            fontSize: 15,
            fontFamily: FONTS.bodyBold,
            color: colors.textPrimary,
          }}
        >
          {t('payouts.manage_accounts')}
        </Text>
        {/* No clamp, and the same 17pt leading as the dashboard. This copy
            wraps to two lines at this width, so `numberOfLines={2}` never
            visibly truncated it — but it made the row one bad translation
            away from the mid-word cut the dashboard row already shipped and
            removed, and it left the two rows on different line boxes. */}
        <Text
          style={{
            fontSize: 12,
            lineHeight: 17,
            fontFamily: FONTS.body,
            color: colors.textSecondary,
            marginTop: SPACING.xs,
          }}
        >
          {t('payouts.manage_accounts_desc')}
        </Text>
      </View>
      <ChevronRight size={18} color={colors.textMuted} strokeWidth={1.7} />
    </Pressable>
  )
}
